/**
 * Feature Access Control Registry
 *
 * Maps FeatureKey to FeatureAccessControl configuration. Single source of
 * truth for who can view/edit feature configs.
 */

import { FeatureKey } from "../schemas/feature-key.schemas.js";
import { ConfigScope } from "../schemas/config-scope.schemas.js";
import type { FeatureAccessControl } from "../schemas/feature-access-control.schemas.js";

/**
 * Default access control for event-scoped features.
 *
 * - Admins toggle `enabled` at global scope.
 * - Admins and event owners edit config at event scope.
 * - Admins and event participants view at event scope.
 */
const EVENT_FEATURE_ACCESS_CONTROL: FeatureAccessControl = {
  enabledEditableAtScope: ConfigScope.GLOBAL,
  enabledEditableBy: { userRoles: ["user:admin"] },
  configEditableAtScope: ConfigScope.EVENT,
  configEditableBy: {
    eventRoles: ["event:owner"],
  },
  visibleAtScope: ConfigScope.EVENT,
  viewableBy: {
    eventRoles: ["event:owner", "event:participant"],
  },
};

/**
 * Access control for host-only event features. Same as
 * `EVENT_FEATURE_ACCESS_CONTROL` but viewable only by event owners — not by
 * participants.
 */
const HOST_FEATURE_ACCESS_CONTROL: FeatureAccessControl = {
  enabledEditableAtScope: ConfigScope.GLOBAL,
  enabledEditableBy: { userRoles: ["user:admin"] },
  configEditableAtScope: ConfigScope.EVENT,
  configEditableBy: {
    eventRoles: ["event:owner"],
  },
  visibleAtScope: ConfigScope.EVENT,
  viewableBy: {
    eventRoles: ["event:owner"],
  },
};

/**
 * Access control for system/infrastructure features. Admin only, global scope
 * only. Not visible to non-admin users.
 */
const SYSTEM_FEATURE_ACCESS_CONTROL: FeatureAccessControl = {
  enabledEditableAtScope: ConfigScope.GLOBAL,
  enabledEditableBy: { userRoles: ["user:admin"] },
  configEditableAtScope: ConfigScope.GLOBAL,
  configEditableBy: { userRoles: ["user:admin"] },
  visibleAtScope: ConfigScope.GLOBAL,
  viewableBy: { userRoles: ["user:admin"] },
};

/**
 * Registry mapping FeatureKey to FeatureAccessControl. All feature keys MUST
 * have an entry here; the `Record<FeatureKey, …>` constraint enforces this.
 */
const FEATURE_ACCESS_CONTROL_MAP: Record<FeatureKey, FeatureAccessControl> = {
  // === Invitation Features (host-only) ===
  [FeatureKey.INVITATIONS]: HOST_FEATURE_ACCESS_CONTROL,
  [FeatureKey.INVITATION_AUTO_INVITE]: HOST_FEATURE_ACCESS_CONTROL,

  // === Import Features (host-only) ===
  [FeatureKey.PARTICIPANTS_IMPORT_CSV_UPLOAD]: HOST_FEATURE_ACCESS_CONTROL,

  // === Intros Features (event-scoped) ===
  [FeatureKey.INTROS]: EVENT_FEATURE_ACCESS_CONTROL,

  // === Porter Notifications (host-only) ===
  // The operator axis over what Porter sends unprompted. Host-only rather than
  // event-scoped-visible: the member-facing control is their own preference, not
  // this switch.
  [FeatureKey.AGENTS_PORTER_NOTIFICATIONS]: HOST_FEATURE_ACCESS_CONTROL,

  // === System/Infrastructure Features (global-only, admin-only) ===
  [FeatureKey.HOST_EVENTS_CREATE]: SYSTEM_FEATURE_ACCESS_CONTROL,

  // === Agent Features (host-only) ===
  [FeatureKey.AGENTS_PORTER]: HOST_FEATURE_ACCESS_CONTROL,

  // The dump records member messages verbatim, so it is admin-only and global
  // like impersonation — not host-flippable alongside the agent it diagnoses.
  [FeatureKey.AGENTS_PORTER_RENDERER_DEBUG_DUMP]: SYSTEM_FEATURE_ACCESS_CONTROL,

  // Which custom fields Porter may read is the host's call for their own
  // event, so the list is editable at EVENT scope like the theme — unlike the
  // debug dump above, which records member messages verbatim and stays admin-only.
  [FeatureKey.AGENTS_PORTER_CUSTOM_FIELDS_CONTEXT]:
    EVENT_FEATURE_ACCESS_CONTROL,

  // Whether Porter may search the directory in conversation is the host's call
  // for their own event, so it is editable at EVENT scope like the registration
  // answers above.
  [FeatureKey.AGENTS_PORTER_DIRECTORY_SEARCH]: EVENT_FEATURE_ACCESS_CONTROL,

  // Whether Porter may send the directory as a spreadsheet in chat is the
  // host's call for their own event, so it is editable at EVENT scope like the
  // directory search above.
  [FeatureKey.AGENTS_PORTER_DIRECTORY_EXPORT]: EVENT_FEATURE_ACCESS_CONTROL,

  // === Theme Features (event-scoped) ===
  [FeatureKey.EVENT_THEME]: EVENT_FEATURE_ACCESS_CONTROL,

  // === Event subdomain (event-scoped, anonymously viewable) ===
  [FeatureKey.EVENT_SUBDOMAIN]: {
    enabledEditableAtScope: ConfigScope.GLOBAL,
    enabledEditableBy: { userRoles: ["user:admin"] },
    configEditableAtScope: ConfigScope.EVENT,
    configEditableBy: {
      eventRoles: ["event:owner"],
    },
    visibleAtScope: ConfigScope.EVENT,
    viewableBy: null,
  },

  // === Directory Features (event-scoped) ===
  [FeatureKey.DIRECTORY_BROWSING]: EVENT_FEATURE_ACCESS_CONTROL,
  [FeatureKey.DIRECTORY_SMART_PREFETCH]: EVENT_FEATURE_ACCESS_CONTROL,
  [FeatureKey.DIRECTORY_EXPORT]: EVENT_FEATURE_ACCESS_CONTROL,

  // === AI Recommendations Features (event-scoped) ===
  [FeatureKey.AI_RECOMMENDATIONS]: EVENT_FEATURE_ACCESS_CONTROL,

  // === Enrichment Features ===
  [FeatureKey.ENRICHMENT]: SYSTEM_FEATURE_ACCESS_CONTROL,
  [FeatureKey.ENRICHMENT_PROCESSOR]: SYSTEM_FEATURE_ACCESS_CONTROL,
  [FeatureKey.AUTOMATIC_ENRICHMENT]: HOST_FEATURE_ACCESS_CONTROL,

  // === LLM Features (system-level) ===

  // === Verification Code Features (system-level) ===
  [FeatureKey.VERIFICATION_CODES]: SYSTEM_FEATURE_ACCESS_CONTROL,

  // === Profile Features (event-scoped) ===
  [FeatureKey.PROFILE_USER_EDIT_LOCKED_FIELDS]: EVENT_FEATURE_ACCESS_CONTROL,
};

/**
 * Feature Access Control Registry. Per Rule domain-logic-exported-defaults: classes for stateful registries; exported functions for stateless logic.
 */
export class FeatureAccessControlRegistry {
  /**
   * Get the access control configuration for a feature.
   */
  static get(featureKey: FeatureKey): FeatureAccessControl {
    // biome-ignore lint/style/noNonNullAssertion: Record<FeatureKey, …> guarantees all keys exist
    return FEATURE_ACCESS_CONTROL_MAP[featureKey]!;
  }

  /**
   * Check if a feature key has access control registered (always true for
   * valid FeatureKey values).
   */
  static has(featureKey: FeatureKey): boolean {
    return featureKey in FEATURE_ACCESS_CONTROL_MAP;
  }
}
